import { useEffect, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import {
  API_ERROR_CODES,
  TOKEN_LAENGE,
  formatiereToken,
  istGueltigesTokenFormat,
  normalisiereToken,
} from '@mcikm-vote/shared';

import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Knopf } from '../../components/Knopf';
import { Markenkopf } from '../../components/Markenkopf';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useTeilnehmerAnmelden } from '../../teilnehmer/teilnehmerApi';

/**
 * Öffentliche Anmeldeseite (§9.1, §19.2).
 *
 * **Was hier zu sehen ist, ist abschließend geregelt:** Anwendungsname, Token-Eingabefeld,
 * Anmeldeschaltfläche, Dark-/Light-Umschaltung und allgemeine Eingabehinweise. Ausdrücklich
 * **nicht** zu sehen sind vorhandene Veranstaltungen, Codes, Namen, Teilnehmerzahlen, der
 * Abstimmungsstatus oder frühere Ergebnisse (§9.1). Deshalb steht hier auch kein Serverstatus und
 * kein Verweis in die Verwaltung.
 *
 * **QR-Code-Vorausfüllung (§8.2, §19.2):** Ein Aufruf mit `?token=…` füllt das Feld vor, meldet
 * aber **nicht** selbsttätig an — die Anmeldung bleibt eine bewusste Handlung. Der Token wird
 * sofort aus der Adresszeile entfernt, damit er nicht im Verlauf, in Lesezeichen oder auf einem
 * geteilten Bildschirm stehen bleibt.
 */
export function AnmeldungSeite(): ReactElement {
  const anmelden = useTeilnehmerAnmelden();

  const [eingabe, setEingabe] = useState('');
  const [uebernahmeAngefragt, setUebernahmeAngefragt] = useState(false);

  useEffect(() => {
    const parameter = new URLSearchParams(window.location.search);
    const vorgabe = parameter.get('token');
    if (vorgabe === null) {
      return;
    }

    setEingabe(formatiereToken(normalisiereToken(vorgabe)));

    // §8.2: der Token verschwindet aus der sichtbaren URL.
    parameter.delete('token');
    const rest = parameter.toString();
    window.history.replaceState(null, '', rest === '' ? window.location.pathname : `?${rest}`);
  }, []);

  const normalisiert = normalisiereToken(eingabe);
  const formatOk = istGueltigesTokenFormat(normalisiert);
  const fehler = anmelden.error;
  const sitzungBesteht = fehler?.code === API_ERROR_CODES.SITZUNG_BESTEHT;

  function absenden(ereignis: FormEvent<HTMLFormElement>, uebernehmen: boolean): void {
    ereignis.preventDefault();
    setUebernahmeAngefragt(uebernehmen);
    anmelden.mutate({ token: normalisiert, ...(uebernehmen ? { uebernehmen: true } : {}) });
  }

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-6 p-6">
      {/* §9.1: Vor der Anmeldung ist der Anwendungsname das Einzige, was hier stehen darf. Er
          steckt als Alternativtext in der Grafik und bleibt damit auch ohne sie lesbar. */}
      <Markenkopf infotext="Anmeldung mit Ihrem Zugangscode" />

      <form
        onSubmit={(ereignis) => {
          absenden(ereignis, false);
        }}
        className="border-rand bg-flaeche flex flex-col gap-4 rounded-xl border p-4 shadow-sm"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="token" className="text-sm font-medium">
            Zugangscode
          </label>
          <input
            id="token"
            name="token"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            required
            value={eingabe}
            aria-describedby="token-hinweis"
            onChange={(ereignis) => {
              setEingabe(ereignis.target.value.toUpperCase());
            }}
            className="border-rand bg-hintergrund min-h-touchflaeche rounded-md border px-3 text-center font-mono text-xl tracking-[0.2em]"
          />
          <p id="token-hinweis" className="text-text-gedaempft text-xs">
            {TOKEN_LAENGE} Zeichen von Ihrer Zugangskarte. Groß- und Kleinschreibung, Leerzeichen
            und Bindestriche spielen keine Rolle.
          </p>
        </div>

        {fehler !== null && !sitzungBesteht && <Fehlermeldung>{fehler.message}</Fehlermeldung>}

        <Knopf
          type="submit"
          breit
          disabled={!formatOk || (anmelden.isPending && !uebernahmeAngefragt)}
        >
          {anmelden.isPending && !uebernahmeAngefragt ? 'Anmelden …' : 'Anmelden'}
        </Knopf>
      </form>

      {sitzungBesteht && (
        <form
          onSubmit={(ereignis) => {
            absenden(ereignis, true);
          }}
          role="alertdialog"
          aria-labelledby="uebernahme-titel"
          className="border-warnung/50 bg-flaeche flex flex-col gap-3 rounded-xl border p-4 shadow-sm"
        >
          <h2 id="uebernahme-titel" className="text-base font-semibold">
            Dieser Zugang wird bereits verwendet
          </h2>
          <p className="text-text-gedaempft text-sm">
            Ihr Zugangscode ist derzeit auf einem anderen Gerät angemeldet. Bei der Übernahme wird
            das andere Gerät sofort abgemeldet. Eine bereits abgegebene Stimme bleibt erhalten und
            kann nicht erneut abgegeben werden.
          </p>
          <Knopf type="submit" breit disabled={anmelden.isPending}>
            {anmelden.isPending ? 'Wird übernommen …' : 'Hier anmelden'}
          </Knopf>
        </form>
      )}

      <ThemeToggle />
    </main>
  );
}
